import { createContext, ReactNode, useContext, useMemo, useState, useEffect } from 'react'
import * as safe from '../lib/safeStorage'

export type AuthContextValue = {
	isAuthenticated: boolean
	isGuest: boolean
	token: string | null
	user: { id: string; email: string; name: string; role?: string; adminPermissions?: any } | null
	guestRolls: number
	login: (t: string, u: { id: string; email: string; name: string; role?: string; adminPermissions?: any }) => void
	logout: () => void
	startGuestSession: () => void
	incrementGuestRolls: () => void
	isLoading: boolean
}

const AuthContext = createContext<AuthContextValue>({ 
	isAuthenticated: false, 
	isGuest: false,
	token: null, 
	user: null, 
	guestRolls: 0,
	login: () => {}, 
	logout: () => {},
	startGuestSession: () => {},
	incrementGuestRolls: () => {},
	isLoading: true
})

export function AuthProvider({ children }: { children: ReactNode }) {
	const [token, setToken] = useState<string | null>(null)
	const [user, setUser] = useState<{ id: string; email: string; name: string; role?: string; adminPermissions?: any } | null>(null)
	const [isGuest, setIsGuest] = useState(false)
	const [guestRolls, setGuestRolls] = useState(0)
	const [isLoading, setIsLoading] = useState(true)

	// Initialize auth state from localStorage (simplified - no server validation on load)
	useEffect(() => {
		const initializeAuth = () => {
			try {
				const storedToken = safe.getItem('ds_token')
				const storedUser = safe.getItem('ds_user')
				const storedGuestRolls = safe.getItem('ds_guest_rolls')
				
				if (storedToken && storedUser) {
					try {
						const parsedUser = JSON.parse(storedUser)
						
						// Set auth state immediately (validation will happen on API calls)
						setToken(storedToken)
						setUser(parsedUser)
						setIsGuest(false)
					} catch (error) {
						// Invalid user data, clear storage
						safe.clearAuthStorage()
					}
				} else if (storedGuestRolls) {
					// Check if there's a guest session
					const rolls = parseInt(storedGuestRolls, 10)
					if (!isNaN(rolls) && rolls > 0) {
						setIsGuest(true)
						setGuestRolls(rolls)
					}
				}
			} catch (error) {
				// Clear any corrupted data
				safe.clearAuthStorage()
			} finally {
				setIsLoading(false)
			}
		}

		// Add small delay to prevent race conditions
		const timer = setTimeout(initializeAuth, 100)
		return () => clearTimeout(timer)
	}, [])

	const login = (t: string, u: { id: string; email: string; name: string; role?: string; adminPermissions?: any }) => {
		console.log('AuthContext: Logging in user', { user: u, hasToken: !!t })
		
		// Validate inputs - check for undefined/null first
		if (!t) {
			console.error('AuthContext: No token provided')
			throw new Error('No authentication token received')
		}
		
		if (!u) {
			console.error('AuthContext: No user object provided')
			throw new Error('No user data received')
		}
		
		if (!u.email) {
			console.error('AuthContext: User object missing email', u)
			throw new Error('User data missing email')
		}
		
		if (!u.name) {
			console.error('AuthContext: User object missing name', u)
			throw new Error('User data missing name')
		}
		
		if (!u.id) {
			console.error('AuthContext: User object missing id', u)
			throw new Error('User data missing id')
		}
		
		setToken(t)
		setUser(u)
		setIsGuest(false)
		setGuestRolls(0)
		safe.setItem('ds_token', t)
		safe.setItem('ds_user', JSON.stringify(u))
		safe.removeItem('ds_guest_rolls')
		console.log('AuthContext: Login complete, isAuthenticated should be true')
	}

	const logout = () => {
		setToken(null)
		setUser(null)
		setIsGuest(false)
		setGuestRolls(0)
		safe.clearAuthStorage()
	}

	const startGuestSession = () => {
		setIsGuest(true)
		setGuestRolls(0)
		safe.setItem('ds_guest_rolls', '0')
	}

	const incrementGuestRolls = () => {
		const newRolls = guestRolls + 1
		setGuestRolls(newRolls)
		safe.setItem('ds_guest_rolls', newRolls.toString())
	}

	const value = useMemo<AuthContextValue>(() => ({
		isAuthenticated: Boolean(token), 
		isGuest,
		token, 
		user, 
		guestRolls,
		login, 
		logout,
		startGuestSession,
		incrementGuestRolls,
		isLoading
	}), [token, user, isGuest, guestRolls, isLoading])

	return (
		<AuthContext.Provider value={value}>
			{children}
		</AuthContext.Provider>
	)
}

export function useAuth() {
	return useContext(AuthContext)
}
